import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Clock,
  FolderSearch,
  GitBranch,
  KeyRound,
  Layers,
  MessageSquare,
  Network,
  Plus,
  Search,
  Send,
  Sparkles,
  TrendingUp,
  Flame,
  Target,
  Users,
  ShieldAlert,
  X,
} from "lucide-react";
import { api, type SessionMessage, type SessionSummary } from "@/lib/api";
import { askStream } from "@/lib/stream";
import {
  runInlineViz,
  runReportRerunViz,
  runNarrateAsk,
  runReportDataViz,
  runIdVizDataViz,
  runUploadedFileDataViz,
  runDataModuleDataViz,
  runPackageDataViz,
  type QuickVizOutcome,
} from "@/lib/viz/quickViz";
import {
  useChatStore,
  type ChatScope,
  type ChatTurn,
  type ToolChart,
  type ToolInvocation,
  type ToolResource,
  type ToolStatus,
  type ToolTable,
  type ToolVisual,
} from "@/store/chat";
import { isChartSpec, type ChartSpec } from "@/lib/chartSpec";
import type { VizRenderPayload } from "@/lib/viz/types";
import { ChatNavigator } from "@/components/ChatNavigator";
import { MessageCard } from "@/components/MessageCard";
import { ContentPicker } from "@/components/ContentPicker";
import { PromptPickerDialog } from "@/components/promptLibrary";
import { displayName } from "@/lib/content";
import { relativeTime } from "@/lib/format";
import { randomUUID } from "@/lib/uuid";
import {
  canDeliver,
  fetchCapabilities,
  type Capabilities,
  type SuggestionRequires,
} from "@/lib/capabilities";

/** localStorage key for the sidebar collapsed flag — persists across reloads. */
const SIDEBAR_COLLAPSED_KEY = "operate.chat.sidebarCollapsed";

type Suggestion = {
  icon: React.ReactNode;
  text: string;
  hint: string;
  requires?: SuggestionRequires;
};

/**
 * Wire shape returned by GET /api/v1/suggested-questions. The
 * backend ships icon as a string (the lucide-react component name)
 * because React components don't survive JSON serialization;
 * {@link iconForName} maps it back to a JSX element.
 */
type ServerSuggestion = {
  id: string;
  icon: string;
  text: string;
  hint: string;
  requires?: SuggestionRequires | null;
};

/**
 * Whitelist of lucide-react icon components the backend may name.
 * Anything off this list falls back to the {@code Sparkles} default,
 * so a new server-side icon can be added safely without breaking
 * older frontends.
 */
function iconForName(name: string | undefined): React.ReactNode {
  switch (name) {
    case "Search": return <Search size={16} />;
    case "Layers": return <Layers size={16} />;
    case "KeyRound": return <KeyRound size={16} />;
    case "TrendingUp": return <TrendingUp size={16} />;
    case "Users": return <Users size={16} />;
    case "Flame": return <Flame size={16} />;
    case "ShieldAlert": return <ShieldAlert size={16} />;
    case "Network": return <Network size={16} />;
    case "GitBranch": return <GitBranch size={16} />;
    default: return <Sparkles size={16} />;
  }
}

/**
 * Starter chips shown on the empty Chat page. Each entry's
 * {@code requires} is treated as a soft hint by {@link canDeliver}: a
 * chip is hidden only when the capabilities snapshot has loaded AND
 * none of the required tools are present AND any required tables are
 * confirmed missing. Hints are deliberately permissive — better to
 * show a prompt that returns "no matching records" than an empty chat
 * page when the capabilities probe is partial. The "always" tier has
 * no requirements at all and acts as a safety net.
 */
const SUGGESTED_QUESTIONS: Suggestion[] = [
  // ── Universal — always show, no tool dependency
  {
    icon: <Search size={16} />,
    text: "Search reports with \"QTD\" in the name",
    hint: "Find content by keyword",
  },
  {
    icon: <Layers size={16} />,
    text: "List all the roles in this Cognos environment",
    hint: "See who's in your security model",
  },
  {
    icon: <KeyRound size={16} />,
    text: "What capabilities are granted in this Cognos system?",
    hint: "System-wide permission breakdown",
  },
  // ── Audit / usage — needs the audit-event mirror
  {
    icon: <TrendingUp size={16} />,
    text: "Which reports haven't been run in the last 90 days?",
    hint: "Identify candidates for archival",
    requires: { tools: ["queryAudit", "listOrphanedObjects"] },
  },
  {
    icon: <Users size={16} />,
    text: "Who are the top 10 most active users this month?",
    hint: "See where usage is concentrated",
    requires: { tools: ["queryAudit", "getUsageStats"] },
  },
  {
    icon: <Flame size={16} />,
    text: "What are the most-run reports in the last 30 days?",
    hint: "Spot the hottest content in the store",
    requires: { tools: ["queryAudit", "getUsageStats"] },
  },
  {
    icon: <ShieldAlert size={16} />,
    text: "Show me failed report runs from the last week",
    hint: "Triage broken jobs",
    requires: { tools: ["queryAudit", "getFailedJobs"] },
  },
  // ── Lens-only — graphs and impact analysis
  {
    icon: <Network size={16} />,
    text: "What's the downstream impact if \"Sample data module\" changes?",
    hint: "Visualize blast radius",
    requires: { tools: ["getImpactAnalysis", "renderImpactAnalysis"] },
  },
  {
    icon: <GitBranch size={16} />,
    text: "Show the lineage of \"UniVisn C10 Cube\"",
    hint: "Render upstream + downstream graph",
    requires: { tools: ["renderLineageGraph", "getDataLineage"] },
  },
];

export function ChatPage() {
  const {
    turns,
    addTurn,
    updateTurn,
    sessionId,
    setSessionId,
    setTurns,
    resetConversation,
    scope,
    setScope,
  } = useChatStore();
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  });
  const listRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
    } catch {
      /* private mode / quota — toggle still works for the session */
    }
  }, [sidebarCollapsed]);

  // Prefill from /saved → "Re-run" navigation. The hash takes the form
  // `#prefill=<encoded question>`; reading + clearing it here keeps the
  // SavedQuestionsPage decoupled from ChatPage's input ref.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    const prefix = "#prefill=";
    if (hash.startsWith(prefix)) {
      try {
        const q = decodeURIComponent(hash.slice(prefix.length));
        if (q) setQuestion(q);
      } catch {
        /* malformed hash — ignore */
      }
      // Replace state so a back-nav doesn't re-trigger the prefill.
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const sessions = useQuery<SessionSummary[]>({
    queryKey: ["sessions"],
    queryFn: async () => (await api.get<SessionSummary[]>("/sessions")).data,
  });

  const { data: capabilities } = useQuery<Capabilities>({
    queryKey: ["capabilities"],
    queryFn: fetchCapabilities,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: history, isFetching: loadingHistory } = useQuery<SessionMessage[]>({
    queryKey: ["session-messages", sessionId],
    enabled: !!sessionId,
    queryFn: async () =>
      (await api.get<SessionMessage[]>(`/sessions/${sessionId}`)).data,
  });

  // Only hydrate turns from server history when the user switches into a
  // different session (sidebar click). Don't replace turns when the stream
  // just assigned us a sessionId for the conversation we're already viewing —
  // doing so wipes the rich in-memory narrative/rows/sql the stream built.
  const hydratedSessionRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!sessionId || !history) return;
    if (hydratedSessionRef.current === sessionId) return;
    if (hydratedSessionRef.current === undefined && turns.length > 0) {
      // Stream-initiated sessionId assignment — keep live turns intact.
      hydratedSessionRef.current = sessionId;
      return;
    }
    hydratedSessionRef.current = sessionId;
    setTurns(messagesToTurns(history));
  }, [sessionId, history, setTurns, turns.length]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [turns.length]);

  // Auto-size composer
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = Math.min(el.scrollHeight, 180) + "px";
  }, [question]);

  const grouped = useMemo(() => groupByDay(sessions.data ?? []), [sessions.data]);

  async function runAsk(q: string) {
    if (!q.trim() || sending) return;
    setSending(true);
    const id = randomUUID();
    const turn: ChatTurn = { id, question: q, status: "streaming" };
    addTurn(turn);
    setQuestion("");
    // Signal the guided tour's "ask a real question" step (firstValueFlow).
    window.dispatchEvent(new CustomEvent("tour:chat-submitted"));

    const scopeCtx = scope
      ? {
          primaryObject: {
            id: null,
            type: scope.objectClass || "folder",
            name: scope.name,
            path: scope.path,
          },
        }
      : undefined;

    try {
      await askStream(
        q,
        sessionId,
        (event, data) => {
          const d = data as Record<string, unknown>;
          switch (event) {
            case "rewritten":
              updateTurn(id, { rewritten: d.standalone as string });
              break;
            case "sql": {
              const prev = useChatStore.getState().turns.find((t) => t.id === id);
              updateTurn(id, {
                sql: (d.sql as { sql?: string })?.sql,
                // Only the legacy direct-SQL pipeline emits a top-level `sql`
                // event. Don't overwrite if the agentic path already flagged
                // this turn as mcp-sourced (a tool can also surface SQL).
                source: prev?.source ?? "api",
              });
              break;
            }
            case "clarification":
              updateTurn(id, { clarification: d.question as string });
              break;
            case "rejected":
              updateTurn(id, {
                rejected: { violations: (d.violations as string[]) ?? [] },
                status: "complete",
              });
              break;
            case "rows": {
              // Backend ships QueryResult = {columns: string[], rows: Map<String,Object>[], rowCount}.
              // UI components (KpiTiles, ResultsTable) expect {columnNames: string[], rows: unknown[][], rowCount}.
              // Normalize here so the rest of the app can stay strictly typed.
              const raw = d.result as {
                columns?: string[];
                columnNames?: string[];
                rows?: unknown;
                rowCount?: number;
              } | undefined;
              if (!raw) break;
              const columnNames = raw.columnNames ?? raw.columns ?? [];
              const rawRows = Array.isArray(raw.rows) ? (raw.rows as unknown[]) : [];
              const rows: unknown[][] = rawRows.map((r) => {
                if (Array.isArray(r)) return r as unknown[];
                if (r && typeof r === "object") {
                  const m = r as Record<string, unknown>;
                  return columnNames.map((c) => m[c]);
                }
                return [];
              });
              updateTurn(id, {
                rows: { columnNames, rows, rowCount: raw.rowCount ?? rows.length },
              });
              break;
            }
            case "execution_failed":
              // Backend emits AskEvent.ExecutionFailed{reason, sqlHash}; earlier
              // drafts used `message`. Accept both so the red callout renders.
              updateTurn(id, {
                errorMessage:
                  (d.reason as string) ??
                  (d.message as string) ??
                  "Query execution failed.",
                status: "error",
              });
              break;
            case "narrative":
              updateTurn(id, { narrative: d.narrative as ChatTurn["narrative"] });
              break;
            // Agentic (CLOUDCRAFT_GATEWAY_USE_MCP=true) events — the backend emits
            // `final` with the assistant's answer text in place of the legacy
            // `narrative` event. Map it into the same narrative shape so
            // MessageCard renders identically across both pipelines.
            case "final":
              updateTurn(id, {
                narrative: {
                  summary: sanitizeAssistantText((d.text as string) ?? ""),
                  keyFindings: [],
                  followUpQuestions: Array.isArray(d.followUpQuestions)
                    ? (d.followUpQuestions as string[])
                    : [],
                  proposedActions: [],
                },
              });
              break;
            case "tool_started": {
              const inv: ToolInvocation = {
                callId: (d.callId as string) ?? "",
                tool: (d.tool as string) ?? "",
                argumentsJson: d.argumentsJson as string | undefined,
                status: "pending",
              };
              const prev = useChatStore.getState().turns.find((t) => t.id === id);
              const existing = prev?.toolCalls ?? [];
              updateTurn(id, {
                toolCalls: [...existing, inv],
                source: prev?.source ?? "mcp",
              });
              break;
            }
            case "tool_visual": {
              const vis: ToolVisual = {
                callId: (d.callId as string) ?? "",
                mimeType: (d.mimeType as string) ?? "image/png",
                dataUrl: (d.dataUrl as string) ?? "",
              };
              if (!vis.dataUrl) break;
              const prev = useChatStore.getState().turns.find((t) => t.id === id);
              const existing = prev?.visuals ?? [];
              updateTurn(id, {
                visuals: [...existing, vis],
                toolCalls: markCallStatus(prev?.toolCalls, vis.callId, "ok"),
              });
              break;
            }
            case "tool_resource": {
              const res: ToolResource = {
                callId: (d.callId as string) ?? "",
                url: (d.url as string) ?? "",
                label: (d.label as string) ?? "Open resource",
              };
              if (!res.url) break;
              const prev = useChatStore.getState().turns.find((t) => t.id === id);
              const existing = prev?.resources ?? [];
              updateTurn(id, {
                resources: [...existing, res],
                toolCalls: markCallStatus(prev?.toolCalls, res.callId, "ok"),
              });
              break;
            }
            case "tool_chart": {
              const callId = (d.callId as string) ?? "";
              const raw = d.specJson as string | undefined;
              if (!raw) break;
              let spec: ChartSpec | null = null;
              try {
                const parsed = JSON.parse(raw);
                if (isChartSpec(parsed)) spec = parsed;
              } catch {
                /* malformed spec — drop the event rather than crash the turn */
              }
              if (!spec) break;
              const tc: ToolChart = { callId, spec };
              const prev = useChatStore.getState().turns.find((t) => t.id === id);
              const existing = prev?.charts ?? [];
              updateTurn(id, {
                charts: [...existing, tc],
                toolCalls: markCallStatus(prev?.toolCalls, callId, "ok"),
              });
              break;
            }
            case "tool_viz_spec": {
              // Tool produced an interactive viz spec (deck.gl for
              // lineage graphs today, vega/echarts in future). Hand it
              // to the existing turn-level vizSpec slot so the standard
              // VisualBlock + VizRenderer path renders it without any
              // new component plumbing. We do NOT wipe an existing
              // vizSpec — a turn may legitimately produce both a chart
              // (from the viz pipeline) and a tool-emitted scene; the
              // most-recent wins.
              const callId = (d.callId as string) ?? "";
              const raw = d.specJson as string | undefined;
              const format = (d.format as string) ?? "deck-gl";
              const vizType = (d.vizType as string) ?? "";
              if (!raw) break;
              const vizSpec: VizRenderPayload = {
                format: format as VizRenderPayload["format"],
                vizType,
                specJson:    raw,
                rows:        [],
                columnNames: [],
              };
              updateTurn(id, {
                vizSpec,
                toolCalls: markCallStatus(
                  useChatStore.getState().turns.find((t) => t.id === id)?.toolCalls,
                  callId, "ok"),
              });
              break;
            }
            case "tool_table": {
              // Server-emitted canonical table. The gateway parsed the
              // tool's structured response and produced columnNames +
              // rows from the actual JSON, so the row count is exact —
              // immune to LLM transcription drift.
              const callId = (d.callId as string) ?? "";
              const raw = d.tableJson as string | undefined;
              if (!raw) break;
              try {
                const parsed = JSON.parse(raw) as {
                  columnNames?: string[];
                  rows?: unknown[][];
                  rowCount?: number;
                };
                if (!Array.isArray(parsed.columnNames) || !Array.isArray(parsed.rows)) break;
                const tt: ToolTable = {
                  callId,
                  columnNames: parsed.columnNames,
                  rows: parsed.rows,
                  rowCount: parsed.rowCount ?? parsed.rows.length,
                };
                const prev = useChatStore.getState().turns.find((t) => t.id === id);
                const existing = prev?.tables ?? [];
                updateTurn(id, {
                  tables: [...existing, tt],
                  toolCalls: markCallStatus(prev?.toolCalls, callId, "ok"),
                });
              } catch {
                /* malformed envelope — drop rather than crash the turn */
              }
              break;
            }
            case "tool_error": {
              // Per-tool failure — do NOT set turn-wide errorMessage. The LLM
              // often recovers by calling a different tool; rendering a red
              // "Something went wrong" for the whole turn would be misleading.
              // The error lives inside the matching invocation record so the
              // ToolCallsStrip can show it inline.
              const prev = useChatStore.getState().turns.find((t) => t.id === id);
              const callId = (d.callId as string) ?? "";
              const toolName = (d.tool as string) ?? "";
              const message = (d.message as string) ?? "tool call failed";
              updateTurn(id, {
                toolCalls: markCallStatus(prev?.toolCalls, callId, "error", message, toolName),
              });
              break;
            }
            case "done": {
              // Non-visual tools don't emit tool_visual/tool_resource, so a
              // clean finish leaves them marked "pending". If the turn
              // reached `done` without a turn-wide error, treat any still-
              // pending invocations as successful — the LLM used their
              // output to produce the final answer.
              const prev = useChatStore.getState().turns.find((t) => t.id === id);
              const finalized = prev?.toolCalls?.map((c) =>
                c.status === "pending" ? { ...c, status: "ok" as const } : c,
              );
              updateTurn(id, { status: "complete", toolCalls: finalized });
              if (d.conversationId && !sessionId) setSessionId(d.conversationId as string);
              if (d.sessionId && !sessionId) setSessionId(d.sessionId as string);
              break;
            }
            case "error":
              updateTurn(id, { errorMessage: d.message as string, status: "error" });
              break;
          }
        },
        undefined,
        scopeCtx,
      );
    } catch (err) {
      updateTurn(id, { errorMessage: String(err), status: "error" });
    } finally {
      setSending(false);
    }
  }

  function onFollowUp(q: string) {
    void runAsk(q);
  }

  /**
   * Shared chat-turn flow for the three viz follow-up paths. Adds a
   * synthetic user turn carrying the intent label, runs the helper,
   * and stamps the resulting Visual onto the assistant turn. Errors
   * land in {@code errorMessage}; success populates {@code vizSpec}
   * + {@code sourceRef} so the next bubble can iterate.
   */
  async function runVizTurn(
    intentLabel: string,
    helper: () => Promise<QuickVizOutcome>,
  ) {
    if (sending) return;
    setSending(true);
    const id = randomUUID();
    addTurn({ id, question: intentLabel, status: "streaming" });
    try {
      const outcome = await helper();
      updateTurn(id, {
        status:        outcome.success ? "complete" : "error",
        // Narrative answers land in `narrative.summary` so the
        // existing markdown renderer + numeric validator picks them
        // up; chart answers land in vizSpec + sourceRef.
        narrative:     outcome.visual?.vizSpec
          ? undefined
          : { summary: outcome.text, keyFindings: [], followUpQuestions: [], proposedActions: [] },
        vizSpec:       outcome.visual?.vizSpec ?? null,
        sourceRef:     outcome.visual?.sourceRef ?? null,
        errorMessage:  outcome.success ? undefined : outcome.text,
      });
    } catch (e) {
      updateTurn(id, { status: "error", errorMessage: e instanceof Error ? e.message : "unknown error" });
    } finally {
      setSending(false);
    }
  }

  /** Feature #1 — refine a chart using its cached rows. */
  function runChartFollowUp(intent: string, columnNames: string[], rows: Array<Array<unknown>>) {
    void runVizTurn(intent, () => runInlineViz(intent, columnNames, rows));
  }

  /** Feature #2 — re-run a Cognos report with new prompt-parameter values. */
  function runRerunWithFilters(
    sourceRef: { kind: string; params: Record<string, unknown> },
    values: Record<string, string | string[]>,
  ) {
    const summary = summariseFilters(values);
    const userMsg = summary === "" ? "Re-run with cleared filters" : `Re-run with ${summary}`;
    void runVizTurn(userMsg, () => runReportRerunViz(sourceRef, values, userMsg));
  }

  /** Feature #3 — analyse the cached data with a narrative question. */
  function runChartAskAboutData(intent: string, columnNames: string[], rows: Array<Array<unknown>>) {
    void runVizTurn(intent, () => runNarrateAsk(intent, columnNames, rows));
  }

  /**
   * "Visualize" affordance for a pinned scope. Dispatches to the
   * right runner based on the object's class:
   *
   * <ul>
   *   <li>report / reportView / query → {@code runReportDataViz}
   *       (ReportService.run via cognos.report.run)</li>
   *   <li>dashboard / exploration → {@code runIdVizDataViz}
   *       (IdVizService.run via cognos.idviz.run, experimental)</li>
   *   <li>uploadedFile → {@code runUploadedFileDataViz}
   *       (ContentManagerService byte fetch + CSV decode via
   *       cognos.uploadedFile.fetch)</li>
   *   <li>dataModule / module → {@code runDataModuleDataViz}
   *       (transient ReportService.runSpecification via
   *       cognos.dataModule.run)</li>
   *   <li>package → {@code runPackageDataViz}
   *       (transient ReportService.runSpecification via
   *       cognos.package.run; this is the path Cognos's spec
   *       engine actually accepts cleanly)</li>
   * </ul>
   *
   * Hidden when no scope is set or the scope isn't a leaf.
   */
  function runVisualizeScope() {
    if (!scope) return;
    // Strip leading underscore so `_package` etc. dispatch the same
    // way as the unprefixed classes — Cognos returns the underscore
    // variant for some namespace-internal packages.
    const cls = (scope.objectClass ?? "").toLowerCase().replace(/^_/, "");
    if (cls === "uploadedfile") {
      void runVizTurn(
        `Visualize uploaded file ${scope.name}`,
        () => runUploadedFileDataViz({ path: scope.path, name: scope.name }),
      );
      return;
    }
    if (cls === "datamodule" || cls === "module") {
      void runVizTurn(
        `Visualize data module ${scope.name}`,
        () => runDataModuleDataViz({ path: scope.path, name: scope.name }),
      );
      return;
    }
    if (cls === "package") {
      void runVizTurn(
        `Visualize package ${scope.name}`,
        () => runPackageDataViz({ path: scope.path, name: scope.name }),
      );
      return;
    }
    const isIdVizObject = cls === "dashboard" || cls === "exploration" || cls === "explorationview";
    if (isIdVizObject) {
      void runVizTurn(
        `Visualize ${cls} ${scope.name}`,
        () => runIdVizDataViz({ path: scope.path, name: scope.name, objectClass: scope.objectClass }),
      );
    } else {
      void runVizTurn(
        `Visualize ${scope.name}`,
        () => runReportDataViz({ path: scope.path, name: scope.name }),
      );
    }
  }

  function onEditQuestion(q: string) {
    // Pre-fill the composer with the original question so the user can
    // rephrase and resend without retyping. Do NOT auto-send — the whole
    // point is that they want to tweak the text first. Focus + select
    // so they can start typing over it or append immediately.
    setQuestion(q);
    const el = textareaRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(q.length, q.length);
        el.scrollTop = el.scrollHeight;
      });
    }
  }

  function startNewConversation() {
    hydratedSessionRef.current = undefined;
    resetConversation();
  }

  return (
    <div
      className={`grid h-[calc(100vh-57px)] w-full transition-[grid-template-columns] duration-200 ${
        sidebarCollapsed ? "grid-cols-[52px,1fr]" : "grid-cols-[280px,1fr]"
      }`}
    >
      {/* Sidebar */}
      <aside className="flex min-h-0 flex-col border-r border-datumcraft-slate/25 bg-white/70">
        {sidebarCollapsed ? (
          <div className="flex flex-col items-center gap-1 px-1 pt-3">
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-navy"
              title="Expand conversations"
              aria-label="Expand conversations"
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={startNewConversation}
              className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-md border border-datumcraft-slate/40 bg-white text-datumcraft-ink hover:border-datumcraft-accentDark hover:text-datumcraft-navy"
              title="Start a new conversation"
              aria-label="Start a new conversation"
            >
              <Plus size={14} />
            </button>
            <div className="mt-3 h-px w-6 bg-datumcraft-slate/25" aria-hidden />
            <div className="flex flex-col items-center gap-1 overflow-y-auto py-2">
              {(sessions.data ?? []).slice(0, 12).map((s) => {
                const active = sessionId === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSessionId(s.id)}
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                      active
                        ? "bg-datumcraft-navy text-white"
                        : "text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-ink"
                    }`}
                    title={s.title ?? s.sessionId.slice(0, 12)}
                    aria-label={s.title ?? "Conversation"}
                  >
                    <MessageSquare size={13} aria-hidden />
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
              <h2 className="text-2xs font-semibold uppercase tracking-wider text-datumcraft-slate">
                Conversations
              </h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={startNewConversation}
                  className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/40 bg-white px-2 py-1 text-xs font-medium text-datumcraft-ink hover:border-datumcraft-accentDark hover:text-datumcraft-navy"
                  title="Start a new conversation"
                >
                  <Plus size={12} /> New
                </button>
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-navy"
                  title="Collapse conversations"
                  aria-label="Collapse conversations"
                >
                  <ChevronLeft size={14} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-2 pb-4">
              {sessions.isLoading && (
                <p className="px-3 py-2 text-xs text-datumcraft-slate">Loading…</p>
              )}
              {!sessions.isLoading && (sessions.data?.length ?? 0) === 0 && (
                <p className="px-3 py-2 text-xs text-datumcraft-slate">No conversations yet.</p>
              )}

              {grouped.map((group) => (
                <div key={group.label} className="mb-3">
                  <div className="px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-datumcraft-slate/80">
                    {group.label}
                  </div>
                  <ul className="space-y-0.5 text-sm">
                    {group.sessions.map((s) => {
                      const active = sessionId === s.id;
                      return (
                        <li key={s.id}>
                          <button
                            onClick={() => setSessionId(s.id)}
                            className={`group flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
                              active
                                ? "bg-datumcraft-navy text-white shadow-sm"
                                : "text-datumcraft-ink hover:bg-datumcraft-mist"
                            }`}
                          >
                            <MessageSquare
                              size={14}
                              className={`mt-0.5 shrink-0 ${
                                active ? "text-white/80" : "text-datumcraft-slate"
                              }`}
                              aria-hidden
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[13px] font-medium">
                                {s.title ?? s.sessionId.slice(0, 12)}
                              </div>
                              <div
                                className={`mt-0.5 flex items-center gap-1 text-2xs ${
                                  active ? "text-white/70" : "text-datumcraft-slate"
                                }`}
                              >
                                <Clock size={10} aria-hidden />
                                {relativeTime(s.updatedAt)}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </aside>

      {/* Main chat */}
      {/*
        min-w-0 on the section + the inner list is load-bearing — without
        it, a wide table or image inside a MessageCard widens the grid
        track instead of triggering the widget's own inner overflow-auto,
        producing a page-level horizontal scrollbar. Grid + flex children
        default to min-width: auto (= content size), so any nested
        widget that wants to be its own scroll container needs every
        ancestor up to the grid track to permit shrinking.
      */}
      <section className="relative flex min-h-0 min-w-0 flex-col">
        <div
          ref={listRef}
          className="flex-1 min-w-0 space-y-5 overflow-y-auto px-6 py-6"
          aria-live="polite"
          aria-busy={sending}
        >
          {loadingHistory && turns.length === 0 && (
            <p className="text-center text-sm text-datumcraft-slate">Loading conversation…</p>
          )}

          {!loadingHistory && turns.length === 0 && (
            <EmptyState onPick={(q) => runAsk(q)} disabled={sending} capabilities={capabilities} />
          )}

          {turns.map((t, idx) => {
            // Only the most-recent assistant turn carrying a vizSpec
            // gets the chart follow-up affordances. Older turns stay
            // read-only so the chat thread doesn't sprout multiple
            // refine/ask inputs on a long conversation.
            const isLatestViz =
              t.vizSpec != null
              && idx === lastVizIdx(turns);
            return (
              <div key={t.id} data-turn-id={t.id} className="scroll-mt-4">
                <MessageCard
                  turn={t}
                  onFollowUp={onFollowUp}
                  onEditQuestion={onEditQuestion}
                  onChartFollowUp={isLatestViz ? runChartFollowUp : undefined}
                  onChartAskAboutData={isLatestViz ? runChartAskAboutData : undefined}
                  onRerunWithFilters={isLatestViz ? runRerunWithFilters : undefined}
                  chartActionsBusy={sending}
                />
              </div>
            );
          })}
        </div>

        <ChatNavigator scrollContainerRef={listRef} turnCount={turns.length} />

        <Composer
          textareaRef={textareaRef}
          question={question}
          setQuestion={setQuestion}
          sending={sending}
          onSend={() => runAsk(question)}
          scope={scope}
          onClearScope={() => setScope(undefined)}
          onOpenPicker={() => setPickerOpen(true)}
          onOpenPromptLibrary={() => setPromptLibraryOpen(true)}
          onVisualize={isScopeLeaf(scope) ? runVisualizeScope : undefined}
        />
      </section>

      <ContentPicker
        open={pickerOpen}
        title="Pin a Cognos scope for this conversation"
        initialPath={scope?.path}
        onCancel={() => setPickerOpen(false)}
        onConfirm={(picked) => {
          setScope({
            path: picked.path,
            name: picked.name,
            objectClass: picked.objectClass,
          });
          setPickerOpen(false);
        }}
      />

      <PromptPickerDialog
        open={promptLibraryOpen}
        onClose={() => setPromptLibraryOpen(false)}
        onPick={(text) => {
          // Pick → fill the composer + fire send. Matches the user's
          // expectation that a library click is a one-action shortcut,
          // not a two-step "insert then edit". The dialog closes itself
          // immediately after onPick fires, but we close-and-then-send
          // here too so the input updates land in state before runAsk
          // reads it (setQuestion is async via React batching).
          setPromptLibraryOpen(false);
          setQuestion(text);
          void runAsk(text);
        }}
      />
    </div>
  );
}

function EmptyState({
  onPick,
  disabled,
  capabilities,
}: {
  onPick: (q: string) => void;
  disabled: boolean;
  capabilities: Capabilities | undefined;
}) {
  // Prefer the corpus-aware suggestions from the backend so chip
  // names reflect the actual content store ("Show the lineage of
  // 'DatumCraft Demo'") instead of demo-corpus placeholders. Falls back
  // to the static SUGGESTED_QUESTIONS array on error / loading /
  // empty response so the empty page never shows zero chips.
  const { data: serverSuggestions } = useQuery({
    queryKey: ["suggested-questions"],
    queryFn: async () => (await api.get<ServerSuggestion[]>("/suggested-questions")).data,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const suggestions: Suggestion[] = serverSuggestions && serverSuggestions.length > 0
    ? serverSuggestions.map((s) => ({
        icon: iconForName(s.icon),
        text: s.text,
        hint: s.hint,
        requires: s.requires ?? undefined,
      }))
    : SUGGESTED_QUESTIONS;
  const visible = suggestions.filter((s) => canDeliver(s.requires, capabilities));
  return (
    <div className="mx-auto mt-6 max-w-3xl">
      <div className="rounded-2xl border border-datumcraft-slate/20 bg-gradient-to-br from-white via-white to-datumcraft-mist/60 p-6 shadow-card">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-datumcraft-accent to-datumcraft-accentDark text-white shadow-sm">
            <Sparkles size={20} aria-hidden />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-datumcraft-navy">
              Ask anything about your Cognos environment
            </h1>
            <p className="text-sm text-datumcraft-slate">
              Grounded in your audit & metadata — every answer cites the rows that back it.
            </p>
          </div>
        </div>

        {visible.length > 0 && (
          <p className="mt-5 text-2xs font-semibold uppercase tracking-wider text-datumcraft-slate">
            Try one of these
          </p>
        )}
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {visible.map((s, i) => (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => onPick(s.text)}
              className="group flex items-start gap-3 rounded-xl border border-datumcraft-slate/25 bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-datumcraft-accentDark hover:shadow-elev disabled:opacity-50"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-datumcraft-accent/15 text-datumcraft-accentDark group-hover:bg-datumcraft-accent/25">
                {s.icon}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-datumcraft-navy">{s.text}</span>
                <span className="mt-0.5 block text-xs text-datumcraft-slate">{s.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Composer({
  textareaRef,
  question,
  setQuestion,
  sending,
  onSend,
  scope,
  onClearScope,
  onOpenPicker,
  onOpenPromptLibrary,
  onVisualize,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  question: string;
  setQuestion: (q: string) => void;
  sending: boolean;
  onSend: () => void;
  scope?: ChatScope;
  onClearScope: () => void;
  onOpenPicker: () => void;
  /** Opens the prompt-library quick-picker dialog. */
  onOpenPromptLibrary: () => void;
  /**
   * When provided + scope is a leaf (report-shaped), surfaces a
   * "Visualize" button next to "Change scope". Clicking it runs the
   * full viz pipeline against the pinned report.
   */
  onVisualize?: () => void;
}) {
  return (
    <form
      className="border-t border-datumcraft-slate/25 bg-white/90 px-6 py-3 backdrop-blur"
      onSubmit={(e) => {
        e.preventDefault();
        onSend();
      }}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {scope ? (
          <span
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-datumcraft-accentDark/30 bg-datumcraft-accent/10 px-2.5 py-1 text-xs text-datumcraft-navy"
            title={scope.path}
          >
            <Target size={12} className="shrink-0 text-datumcraft-accentDark" aria-hidden />
            <span className="truncate font-medium">{displayName(scope.path)}</span>
            {scope.objectClass && (
              <span className="text-2xs uppercase tracking-wide text-datumcraft-slate">
                {scope.objectClass}
              </span>
            )}
            <button
              type="button"
              onClick={onClearScope}
              aria-label="Clear scope"
              className="ml-0.5 rounded-full p-0.5 text-datumcraft-slate hover:bg-white hover:text-datumcraft-ink"
            >
              <X size={11} />
            </button>
          </span>
        ) : (
          <span className="text-2xs text-datumcraft-slate">
            No scope — questions run across the whole content store.
          </span>
        )}
        <button
          type="button"
          onClick={onOpenPicker}
          className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-xs text-datumcraft-ink hover:border-datumcraft-accentDark"
        >
          <FolderSearch size={12} aria-hidden />
          {scope ? "Change scope" : "Set scope"}
        </button>
        <button
          type="button"
          onClick={onOpenPromptLibrary}
          disabled={sending}
          className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-xs text-datumcraft-ink hover:border-datumcraft-accentDark disabled:cursor-not-allowed disabled:opacity-50"
          title="Browse the prompt library and send a curated question"
        >
          <BookOpen size={12} aria-hidden />
          Prompts
        </button>
        {onVisualize && (
          <button
            type="button"
            onClick={onVisualize}
            disabled={sending}
            className="inline-flex items-center gap-1 rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-900 hover:border-indigo-400 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
            title={visualizeButtonTooltip(scope)}
          >
            <Sparkles size={12} aria-hidden />
            Visualize
          </button>
        )}
      </div>
      <label className="sr-only" htmlFor="q">
        Your question
      </label>
      <div data-tour-id="chat-input" className="group flex items-end gap-2 rounded-xl border border-datumcraft-slate/30 bg-white px-3 py-2 shadow-card focus-within:border-datumcraft-accentDark focus-within:shadow-focus">
        <textarea
          ref={textareaRef}
          id="q"
          rows={1}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder="Ask about reports, users, permissions, schedules…"
          className="flex-1 resize-none border-0 bg-transparent text-sm text-datumcraft-ink placeholder:text-datumcraft-slate focus:outline-none"
        />
        <button
          type="submit"
          disabled={sending || !question.trim()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-datumcraft-navy px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-datumcraft-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sending ? (
            <>
              <span className="thinking-dots" aria-hidden>
                <span /><span /><span />
              </span>
              <span>Sending</span>
            </>
          ) : (
            <>
              <Send size={14} aria-hidden />
              <span>Send</span>
            </>
          )}
        </button>
      </div>
      <p className="mt-1.5 text-2xs text-datumcraft-slate">
        <kbd className="rounded border border-datumcraft-slate/30 bg-datumcraft-mist px-1 py-px text-[10px] font-medium">
          Enter
        </kbd>{" "}
        to send ·{" "}
        <kbd className="rounded border border-datumcraft-slate/30 bg-datumcraft-mist px-1 py-px text-[10px] font-medium">
          Shift + Enter
        </kbd>{" "}
        for a new line
      </p>
    </form>
  );
}

/**
 * True iff the pinned scope is a single leaf object (a report,
 * dashboard, exploration, etc.) — not a folder/package. The
 * "Visualize" button only makes sense on leaf scopes; folder scopes
 * have many distinct reports and the runReportDataViz helper would
 * have nothing to point at.
 */
/**
 * Per-class tooltip for the "Visualize" button. Default copy refers
 * to "this report" which is misleading on uploaded-file and
 * data-module scopes — the action is the same shape but the
 * underlying fetch path differs.
 */
function visualizeButtonTooltip(scope: ChatScope | undefined): string {
  // Strip leading underscore: Cognos uses `_package`, `_namespace`
  // etc. for namespace-internal variants. They're functionally
  // equivalent to the unprefixed classes for viz purposes.
  const cls = (scope?.objectClass ?? "").toLowerCase().replace(/^_/, "");
  if (cls === "uploadedfile") return "Decode this uploaded file and visualize its rows";
  if (cls === "datamodule" || cls === "module") return "Query this data module and visualize the result";
  if (cls === "package") return "Run a transient query against this package and visualize the result";
  if (cls === "dashboard" || cls === "exploration" || cls === "explorationview") {
    return "Run this dashboard / exploration and visualize the result (experimental)";
  }
  return "Run this report and visualize the result";
}

function isScopeLeaf(scope: ChatScope | undefined): boolean {
  if (!scope) return false;
  // Strip leading underscore so namespace-internal variants
  // (`_package`, `_namespace`, etc.) match the same way as the
  // public class names. The user's "Av Finance _PACKAGE" chip
  // wasn't matching `package` exactly — Cognos returns `_package`
  // for some package objects depending on which namespace they
  // live under.
  const cls = (scope.objectClass ?? "").toLowerCase().replace(/^_/, "");
  return cls === "report"
    || cls === "reportview"
    || cls === "dashboard"        // IdVizService — experimental
    || cls === "exploration"      // IdVizService — experimental
    || cls === "explorationview"  // IdVizService — experimental
    || cls === "query"
    // Phase-1/2 (2026-05-11): viz support for uploaded files (CSV
    // bytes decoded via cognos.uploadedFile.fetch) and data modules
    // (transient one-shot query via cognos.dataModule.run). The chat's
    // "Visualize" button dispatches to runUploadedFileDataViz /
    // runDataModuleDataViz respectively — see runVisualizeScope().
    || cls === "uploadedfile"
    || cls === "datamodule"
    || cls === "module"
    // 2026-05-12: FM packages. Cognos's runSpecification accepts a
    // <modelPath> pointing at /content/.../package/model[@name='model']
    // — the canonical shape its CM resolver was built for — so the
    // path is clean for packages where it's a wrap-mess for modules.
    || cls === "package";
}

/**
 * Index of the latest turn that carries a viz-pipeline chart. Used to
 * gate the chart follow-up affordances ({@code onChartFollowUp},
 * {@code onChartAskAboutData}, {@code onRerunWithFilters}) to the most
 * recent visualization in the thread so older bubbles stay read-only.
 */
function lastVizIdx(turns: ChatTurn[]): number {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i].vizSpec) return i;
  }
  return -1;
}

/**
 * Render a Re-run-with-filters parameter map as a short
 * "p_Region=Italy, p_Year=2024" string for the user-side chat bubble.
 * Multi-valued parameters collapse to "p_X=[a, b]". Truncates at a
 * sane length so a 30-prompt report doesn't produce an unreadable
 * bubble.
 */
function summariseFilters(values: Record<string, string | string[]>): string {
  const parts: string[] = [];
  for (const [name, v] of Object.entries(values)) {
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      parts.push(`${name}=[${v.join(", ")}]`);
    } else if (v && v.trim().length > 0) {
      parts.push(`${name}=${v}`);
    }
  }
  const joined = parts.join(", ");
  return joined.length > 200 ? joined.slice(0, 200) + "…" : joined;
}

function groupByDay(
  sessions: SessionSummary[],
): Array<{ label: string; sessions: SessionSummary[] }> {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86_400_000;
  const buckets: Record<string, SessionSummary[]> = {
    Today: [],
    Yesterday: [],
    "This week": [],
    Earlier: [],
  };

  const sorted = [...sessions].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  for (const s of sorted) {
    const t = new Date(s.updatedAt).getTime();
    if (t >= startOfDay) buckets.Today.push(s);
    else if (t >= startOfDay - dayMs) buckets.Yesterday.push(s);
    else if (t >= startOfDay - dayMs * 7) buckets["This week"].push(s);
    else buckets.Earlier.push(s);
  }

  return (["Today", "Yesterday", "This week", "Earlier"] as const)
    .filter((k) => buckets[k].length > 0)
    .map((k) => ({ label: k, sessions: buckets[k] }));
}

/**
 * The LLM occasionally emits raw tool-use XML (`<function_calls>…</function_calls>`
 * or `<use_mcp_server>…</use_mcp_server>`) inside the assistant-visible text
 * instead of actually invoking the tool. That shouldn't happen when the
 * tool-bridge is healthy, but when it does we don't want the user to see
 * pseudo-XML in their answer. Strip those blocks (tag-agnostic within the
 * known-bad names) and collapse the whitespace they leave behind.
 */
function sanitizeAssistantText(text: string): string {
  if (!text) return text;
  // Known pseudo-XML shapes the model sometimes narrates instead of actually
  // firing a tool: <function_calls>…</function_calls>, <use_mcp_server>…,
  // <use_mcp_tool>…, plus the antml:-prefixed Anthropic syntax. If a new
  // variant shows up, add its tag name to TAGS rather than building another
  // one-off regex.
  const TAGS = ["function_calls", "use_mcp_server", "use_mcp_tool", "antml:function_calls"];
  let cleaned = text;
  for (const tag of TAGS) {
    const esc = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`<${esc}\\b[^>]*>[\\s\\S]*?<\\/${esc}>`, "gi");
    cleaned = cleaned.replace(re, "");
  }
  return cleaned.replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Immutably update the invocation matching `callId`. If no invocation carries
 * that callId (e.g. tool_error arrived for a call we never saw started — rare,
 * but happens on session-pool-level failures), synthesize a placeholder so the
 * error still renders in the strip.
 */
function markCallStatus(
  calls: ToolInvocation[] | undefined,
  callId: string,
  status: ToolStatus,
  errorMessage?: string,
  tool?: string,
): ToolInvocation[] {
  const existing = calls ?? [];
  const idx = existing.findIndex((c) => c.callId === callId);
  if (idx < 0) {
    if (status !== "error") return existing;
    return [
      ...existing,
      { callId, tool: tool ?? "(unknown)", status, errorMessage },
    ];
  }
  const next = existing.slice();
  next[idx] = { ...next[idx], status, errorMessage: errorMessage ?? next[idx].errorMessage };
  return next;
}

function messagesToTurns(messages: SessionMessage[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.role !== "user") continue;
    const next = messages[i + 1];
    const answer = next && next.role !== "user" ? next : undefined;
    turns.push({
      id: String(m.id),
      question: m.content,
      status: "complete",
      narrative: answer
        ? { summary: answer.content, keyFindings: [], followUpQuestions: [], proposedActions: [] }
        : undefined,
    });
  }
  return turns;
}
